import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostBinding,
  inject,
  input,
  Input,
  OnInit,
  PLATFORM_ID,
  Signal,
  signal
} from '@angular/core';
import {ListPage, ListPageTypeTypes,} from '@core/interfaces/generated/graphql';
import {SearchPageComponent} from '../../../page/components/search-page/search-page.component';
import {GroupVoucherService} from '@core/services/groupVoucher.service';
import {isPlatformBrowser} from '@angular/common';
import {AlgoliaService, CustomProductListFilter, CustomProductType} from '@core/services/algolia.service';
import {StoryblokBaseComponent} from '../storyblok-base.component';
import {AlgoliaFacetFilter} from '@core/types/algolia-filter';
import {LoadingIconComponent} from '@layout/components/general/loading-icon/loading-icon.component';
import {TrackEventService} from '@core/services/track-event.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import dayjs from 'dayjs';

@Component({
  selector: 'app-storyblok-algolia-filter',
  templateUrl: './storyblok-algolia-filter.component.html',
  styleUrls: ['./storyblok-algolia-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SearchPageComponent, LoadingIconComponent]
})
export class StoryblokAlgoliaFilterComponent extends StoryblokBaseComponent implements OnInit {
  private platformId = inject(PLATFORM_ID);
  private algoliaService = inject(AlgoliaService);
  private groupVoucherService = inject(GroupVoucherService);
  private trackEventService = inject(TrackEventService);
  private modalService = inject(NgbModal);


  filter = signal<ListPage | null>(null);
  loading = signal(true);

  readonly elementId = input<any>();
  readonly general_styling = input<any>();
  readonly filter_country = input<string[]>();
  readonly filter_facet_types = input<string[]>();
  readonly filter_attribute_set = input<string[]>();
  readonly filter_district = input<string[]>();
  readonly filter_area = input<string[]>();
  readonly filter_producer = input<string[]>();
  readonly filter_field_grapes = input<string[]>();
  readonly filter_field_klassikere = input<string[]>();
  readonly filter_not_wine = input<string[]>();
  readonly filter_categories = input<string[]>();
  readonly filter_field_non_alcoholic = input<string[]>();
  readonly filter_on_discount = input<boolean>();
  readonly filter_field_food = input<string[]>();
  readonly filter_visma_ids = input<string[]>();
  readonly filter_sparkling_type = input<string[]>();
  readonly filter_port_type = input<string[]>();
  readonly filter_reviewer = input<string[]>();
  readonly filter_tasting_notes = input<string[]>();
  readonly filter_sweetness = input<string>();
  readonly filter_wine_body = input<string>();
  readonly filter_wine_power = input<string>();
  readonly filter_content = input<string>();
  readonly filter_points = input<string[]>();
  readonly filter_organic_biodynamic = input<string[]>();
  readonly filter_season_code = input<string[]>();
  readonly filter_price_min = input<string>();
  readonly filter_price_max = input<string>();
  readonly filter_published_within_days = input<number>();
  @HostBinding('class') class: any;

  only_show_leftovers = input<boolean>(false);
  voucher_list = input<boolean>(false);
  hide_sold_out = input<boolean>(false);
  discount_expires_within = input<string>();
  groupVoucherSignal = this.groupVoucherService.groupVoucherSignal;

  @Input()
  set id(value: any) {
    this.hostId = value;
  }

  @HostBinding('attr.id') hostId: any;

  filters = computed(() => {
    const algoliaFacets = this.algoliaService.algoliaFacets();
    if (this.voucher_list() && !isPlatformBrowser(this.platformId)) {
      return;
    }
    return this.generateFilter(algoliaFacets);
  });

  loadingGroupVoucher = computed(() => this.voucher_list() && this.groupVoucherSignal().loading);

  ngOnInit(): void {
    this.class = 'col-12';
  }

  openGroupVoucherModal() {
    this.trackEventService.trackClickEvent('voucher_modal_open', {eventSourceName: 'storyblok-algolia-filter'});
    import('../../../../layout/components/general/header/components/header-middle/components/header-middle-voucher-modal/header-middle-voucher-modal.component').then(comp => {
      this.modalService.open(comp.HeaderMiddleVoucherModalComponent);
    });
  }

  generateFilter(filters: AlgoliaFacetFilter[]): CustomProductListFilter {

    const types = [
      ...this.inputToFilter(filters, 'filter_country', 'country'),
      ...this.inputToFilter(filters, 'filter_facet_types', 'facet_types'),
      ...this.inputToFilter(filters, 'filter_attribute_set', 'attribute_set'),
      ...this.inputToFilter(filters, 'filter_district', 'district'),
      ...this.inputToFilter(filters, 'filter_area', 'area'),
      ...this.inputToFilter(filters, 'filter_producer', 'producer'),
      ...this.inputToFilter(filters, 'filter_field_grapes', 'field_grapes'),
      ...this.inputToFilter(filters, 'filter_field_klassikere', 'field_klassikere'),
      ...this.inputToFilter(filters, 'filter_field_non_alcoholic', 'field_non_alcoholic'),
      ...this.inputToFilter(filters, 'filter_on_discount', 'on_discount'),
      ...this.inputToFilter(filters, 'filter_not_wine', 'field_non_wine'),
      ...this.inputToFilter(filters, 'filter_categories', 'categories'),
      ...this.inputToFilter(filters, 'filter_field_food', 'field_food'),
      ...this.inputToFilter(filters, 'filter_visma_ids', 'visma_id'),
      ...this.inputToFilter(filters, 'filter_sparkling_type', 'sparkling_type'),
      ...this.inputToFilter(filters, 'filter_port_type', 'port_type'),
      ...this.inputToFilter(filters, 'filter_reviewer', 'wine_ratings.reviewer'),
      ...this.inputToFilter(filters, 'filter_tasting_notes', 'tasting_notes'),
      ...this.inputToFilter(filters, 'filter_sweetness', 'sweetness_label'),
      ...this.inputToFilter(filters, 'filter_wine_body', 'wine_body_label'),
      ...this.inputToFilter(filters, 'filter_wine_power', 'wine_power_label'),
      ...this.inputToFilter(filters, 'filter_content', 'content_label'),
      ...this.inputToFilter(filters, 'filter_points', 'points'),
      ...this.inputToFilter(filters, 'filter_organic_biodynamic', 'organic_biodynamic'),
      ...this.inputToFilter(filters, 'filter_season_code', 'season_code'),
    ] as CustomProductType[];

    if (this.hide_sold_out()) {
      types.push({
        attribute: 'commerce_stock_bool',
        name: [1] as any
      });
    }

    if (this.only_show_leftovers()) {
      types.push({
        attribute: 'is_leftover',
        name: [true] as any
      });
    }

    if (this.discount_expires_within()) {
      const maxDate = dayjs().add(parseInt(this.discount_expires_within() ?? ''), 'h').unix();
      if (!isNaN(maxDate) ) {
        types.push({
          attribute: 'discount_expiration_date',
          name: '',
          min: dayjs().unix(),
          max: maxDate
        })
      }
    }

    if (this.filter_published_within_days()) {
      const days = this.filter_published_within_days()!;
      if (!isNaN(days) && days > 0) {
        types.push({
          attribute: 'published_date',
          name: '',
          min: dayjs().subtract(days, 'd').unix(),
          max: dayjs().unix()
        });
      }
    }

    // Handle price range filter
    const minStr = this.filter_price_min();
    const maxStr = this.filter_price_max();
    const min = minStr !== undefined ? parseInt(minStr, 10) : undefined;
    const max = maxStr !== undefined ? parseInt(maxStr, 10) : undefined;

    if ((min !== undefined && !isNaN(min)) || (max !== undefined && !isNaN(max))) {
      types.push({
        attribute: 'default_price',
        name: '',
        min: min !== undefined && !isNaN(min) ? min : undefined,
        max: max !== undefined && !isNaN(max) ? max : undefined
      });
    }

    const filter: CustomProductListFilter = {
      objectIds: [],
      types,
      title: '',
      storyblok: undefined
    };

    if (!this.voucher_list()) {
      return filter;
    }
    const voucherFilter = this.groupVoucherService.voucherSearchPageFilter();
    return {
      ...filter,
      objectIds: voucherFilter.objectIds ?? []
    }
  }

  protected inputToFilter(filters: AlgoliaFacetFilter[], key: keyof this, attribute: string): ListPageTypeTypes[] {
    const facet = filters.find(value => value.algoliaAttribute === attribute);
    if (!facet) {
      return [];
    }
    if (facet.instantSearchType === 'toggle') {
      return [
        {
          attribute,
          name: (this[key] as Signal<any>)()
        }
      ];
    } else if (facet.instantSearchType === 'ratings-filter') {
      // Handle points filter: convert list of points to min-max range
      let value = (this[key] as Signal<string | string[]>)();
      if (!value || (Array.isArray(value) && value.length === 0)) {
        return [];
      }
      if (typeof value === 'string') {
        value = value.split(',').map(d => d.trim());
      }
      // Convert string values to numbers and sort them
      const numericValues = value.map(v => parseInt(v, 10)).filter(v => !isNaN(v)).sort((a, b) => a - b);
      if (numericValues.length === 0) {
        return [];
      }
      // Return a single filter with min and max
      return [
        {
          attribute,
          name: '',
          min: numericValues[0],
          max: numericValues[numericValues.length - 1]
        } as any
      ];
    } else {
      let value = (this[key] as Signal<string | string[]>)();
      if (typeof value === 'string') {
        value = value.split(',').map(d => d.trim());
      }
      return value?.map(name => ({attribute, name})) ?? [];
    }
  }
}
